import React, { useEffect } from 'react';

import { AuthGuard, GuestGuard } from '@components/operator/AuthGuard';
import { operatorRoutes, getPermissionGateResult, getPlanGateResult } from '@components/operator/menuConfig';
import OperatorLayout from '@components/operator/OperatorLayout';
import { useOperatorAuth } from '@contexts/operator/OperatorAuthContext';
import { usePlan } from '@contexts/operator/PlanContext';
import { useStorageMode } from '@contexts/operator/StorageModeContext';
import OperatorLogin from '@pages/operator/Login';
import TotpSetup from '@pages/operator/TotpSetup';
import OperatorInvitationAccept from '@pages/public/OperatorInvitationAccept';
import ErrorBoundary from '@shared/ErrorBoundary';
import RouteErrorBoundary from '@shared/RouteErrorBoundary';
import { message, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom';

// Fallback 組件
const LoadingFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <Spin size="large" />
  </div>
);

// Outer gate: RBAC. If the operator lacks `requiredPermission`, show a
// permission_denied toast and redirect to /dashboard. Inner PlanGate never
// mounts in that case.
export const PermissionGate = ({ route, children }) => {
  const { permissions } = useOperatorAuth();
  const { t } = useTranslation();
  const location = useLocation();

  const result = getPermissionGateResult(route, { permissions });

  useEffect(() => {
    if (!result.ok) {
      message.info(t('plan.permission_denied'));
    }
    // 結果翻轉或路徑變更時才觸發一次 toast
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.ok, location.pathname]);

  if (!result.ok) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Inner gate: plan / feature / standard / storageMode. Reached only after
// PermissionGate passed, so the failure reason is never "permission".
export const PlanGate = ({ route, children }) => {
  const { enabledFeatures, isStandard, isEnterprise } = usePlan();
  const { mode: storageMode } = useStorageMode();
  const { t } = useTranslation();
  const location = useLocation();

  const result = getPlanGateResult(route, { enabledFeatures, isStandard, isEnterprise, storageMode });

  useEffect(() => {
    if (!result.ok) {
      let msgKey;
      if (result.reason === 'standard') {
        msgKey = 'plan.merchant_settings_unified';
      } else if (result.reason === 'enterprise') {
        // Enterprise 客戶嘗試直接打方案選擇 URL → 提示為合約方案
        msgKey = 'plan.enterprise_contact_manager';
      } else {
        msgKey = 'plan.feature_not_available';
      }
      message.info(t(msgKey));
    }
    // 結果翻轉或路徑變更時才觸發一次 toast
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.ok, result.reason, location.pathname]);

  if (!result.ok) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// 建立路由配置
export const createOperatorRouter = () => {
  return createBrowserRouter(
    [
      {
        path: '/login',
        element: (
          <ErrorBoundary showDetails={import.meta.env.DEV} homeUrl="/operator">
            <GuestGuard>
              <OperatorLogin />
            </GuestGuard>
          </ErrorBoundary>
        ),
        HydrateFallback: LoadingFallback,
      },
      {
        path: '/invitations/:token',
        element: (
          <ErrorBoundary showDetails={import.meta.env.DEV} homeUrl="/operator">
            <GuestGuard>
              <OperatorInvitationAccept />
            </GuestGuard>
          </ErrorBoundary>
        ),
        HydrateFallback: LoadingFallback,
      },
      {
        path: '/totp-setup',
        element: (
          <ErrorBoundary showDetails={import.meta.env.DEV} homeUrl="/operator">
            <TotpSetup />
          </ErrorBoundary>
        ),
        HydrateFallback: LoadingFallback,
      },
      {
        path: '/',
        element: (
          <ErrorBoundary showDetails={import.meta.env.DEV} homeUrl="/operator">
            <AuthGuard>
              <OperatorLayout />
            </AuthGuard>
          </ErrorBoundary>
        ),
        HydrateFallback: LoadingFallback,
        children: operatorRoutes.map((route) => ({
          path: route.path,
          element: (
            <ErrorBoundary showDetails={import.meta.env.DEV} homeUrl="/operator">
              <PermissionGate route={route}>
                <PlanGate route={route}>
                  {React.createElement(route.component, route.componentProps)}
                </PlanGate>
              </PermissionGate>
            </ErrorBoundary>
          ),
          loader: route.loader,
          errorElement: <RouteErrorBoundary showDetails={import.meta.env.DEV} />,
        })).concat([
          {
            index: true,
            element: <Navigate to="/dashboard" replace />,
          },
          {
            path: '*',
            element: <Navigate to="/dashboard" replace />,
          },
        ]),
      },
    ],
    {
      basename: '/operator',
      hydrateFallbackElement: (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <Spin size="large" />
        </div>
      ),
    }
  );
};
